import {
  moneyMovementTotals,
  negateMoney,
  reconcileMoneyParts,
  sumMoneyParts,
} from '@shared/lib/money/display-reconciliation'
import type { DisplayMoney } from '@shared/lib/money/display-reconciliation'
import { MoneyValue } from '../MoneyValue/MoneyValue'
import { body, container, headline } from './MoneyReconciliation.css'

export function MoneyReconciliation({
  total,
  parts,
  complete = true,
  label = 'Conferência de valores',
}: {
  total: DisplayMoney
  parts: readonly DisplayMoney[]
  complete?: boolean
  label?: string
}) {
  if (!complete) return null
  const result = reconcileMoneyParts(total, parts)
  if (result.kind !== 'mismatch') return null
  return (
    <div className={container} role="note" aria-label={label}>
      <span className={body}>Há uma diferença entre os itens e o total informado.</span>
    </div>
  )
}

export function MoneyMovementSummary({
  movements,
}: {
  movements: readonly { direction: string; amount: DisplayMoney }[]
}) {
  const totals = moneyMovementTotals(movements)
  if (totals.length === 0) return null
  const labels = {
    IN: 'Entradas carregadas',
    OUT: 'Saídas carregadas',
    HELD: 'Retenções carregadas',
  }
  return (
    <section className={container} aria-label="Totais dos eventos carregados">
      {totals.map(({ direction, currency, total, parts }) => (
        <div key={`${currency}:${direction}`}>
          <div className={headline}>
            <span>{labels[direction]}</span>
            <MoneyValue {...total} size="sm" />
          </div>
          <MoneyReconciliation total={total} parts={parts} />
        </div>
      ))}
      <MoneyListSummary
        values={totals.map(({ direction, total }) =>
          direction === 'OUT' ? negateMoney(total) : total,
        )}
        label="Saldo das movimentações carregadas"
      />
      <span className={body}>
        Inclui reservas comerciais. Não representa saldo Stripe nem previsão bancária.
      </span>
    </section>
  )
}

export function MoneyListSummary({
  values,
  label,
}: {
  values: readonly DisplayMoney[]
  label: string
}) {
  return (
    <>
      {[...new Set(values.map((value) => value.currency))].map((currency) => {
        const parts = values.filter((value) => value.currency === currency)
        const total = sumMoneyParts(parts, currency)
        return total ? (
          <section key={currency} className={container} aria-label={label}>
            <div className={headline}>
              <span>{label}</span>
              <MoneyValue {...total} size="sm" />
            </div>
            <MoneyReconciliation total={total} parts={parts} />
          </section>
        ) : null
      })}
    </>
  )
}
